import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import React, { useEffect, useMemo, useRef } from "react";

const BLOCK_TYPE_ICONS: Record<string, string> = {
  chart: "bar_chart",
  metric_card: "speed",
  data_table: "table_chart",
  ai_image: "image",
  ai_insights: "psychology",
  section_header: "title",
  text_block: "text_fields",
};

export const getBlockTypeIcon = (type: string): string => BLOCK_TYPE_ICONS[type] ?? "widgets";

const TEXT_BLOCK_TITLE_SNIPPET_MAX = 30;

/** Plain-text-ish prefix of markdown for display (mentions, picker, block refs). */
function textBlockTitleSnippetFromMarkdown(markdown: string): string | undefined {
  const snippet = markdown
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/[`*_~]+/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, TEXT_BLOCK_TITLE_SNIPPET_MAX);
  return snippet.length > 0 ? snippet : undefined;
}

const getBlockTitle = (block: WebDashboardContentSpecBlocksInner): string => {
  if (block.type === "metric_card") {
    return block.label;
  }
  if (block.type === "section_header") {
    return block.title;
  }
  if (block.type === "ai_image") {
    return block.title || "Image";
  }
  const titled = block as { title?: string };
  if (titled.title) {
    return titled.title;
  }
  if (block.type === "text_block") {
    const fromMarkdown = textBlockTitleSnippetFromMarkdown(block.markdown);
    if (fromMarkdown) {
      return fromMarkdown;
    }
    return "Text block";
  }
  return block.type;
};

export interface BlockAutocompleteDropdownProps {
  blocks: WebDashboardContentSpecBlocksInner[];
  filterText: string;
  onSelect: (blockId: string, blockTitle: string, blockType: string) => void;
  highlightedIndex: number;
  onHighlightedIndexChange: (index: number) => void;
  showHeader?: boolean;
  onClose?: () => void;
}

const DropdownHeader: React.FC<{ onClose?: () => void }> = ({ onClose }) => (
  <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
    <span className="text-sm font-medium text-gray-700">Edit existing block</span>
    {onClose && (
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        onMouseDown={e => {
          e.preventDefault();
          onClose();
        }}
        aria-label="Close"
      >
        <span className="material-icons !text-lg">close</span>
      </button>
    )}
  </div>
);

export const BlockAutocompleteDropdown: React.FC<BlockAutocompleteDropdownProps> = ({
  blocks,
  filterText,
  onSelect,
  highlightedIndex,
  onHighlightedIndexChange,
  showHeader = false,
  onClose,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const filteredBlocks = useMemo(() => {
    const query = filterText.toLowerCase();
    return blocks.filter(b => {
      const title = getBlockTitle(b).toLowerCase();
      return title.includes(query);
    });
  }, [blocks, filterText]);

  useEffect(() => {
    if (highlightedIndex >= filteredBlocks.length) {
      onHighlightedIndexChange(Math.max(0, filteredBlocks.length - 1));
    }
  }, [filteredBlocks.length, highlightedIndex, onHighlightedIndexChange]);

  useEffect(() => {
    const container = listRef.current;
    if (!container) {
      return;
    }
    const scrollContainer = container.querySelector("[data-scroll-list]");
    if (!scrollContainer) {
      return;
    }
    const item = scrollContainer.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  if (filteredBlocks.length === 0) {
    return (
      <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
        {showHeader && <DropdownHeader onClose={onClose} />}
        <div className="p-3 text-sm text-gray-500" role="status" aria-live="polite">
          No matching blocks
        </div>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden"
    >
      {showHeader && <DropdownHeader onClose={onClose} />}
      <div data-scroll-list role="listbox" className="max-h-80 overflow-y-auto overscroll-contain">
        {filteredBlocks.map((block, index) => {
          const title = getBlockTitle(block);
          const isHighlighted = index === highlightedIndex;

          return (
            <button
              key={block.block_id}
              type="button"
              role="option"
              aria-selected={isHighlighted}
              className={
                "w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors cursor-pointer " +
                (isHighlighted ? "bg-gray-50 " : "hover:bg-gray-50 ")
              }
              onMouseDown={e => {
                e.preventDefault();
                onSelect(block.block_id, title, block.type);
              }}
              onMouseEnter={() => onHighlightedIndexChange(index)}
            >
              <span className="material-icons text-blue-600 !text-lg">
                {getBlockTypeIcon(block.type)}
              </span>
              <span className="truncate flex-1 text-gray-900 font-medium">{title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Utility: number of filtered items for a given blocks + filterText (used for keyboard nav bounds). */
export const getFilteredBlockCount = (
  blocks: WebDashboardContentSpecBlocksInner[],
  filterText: string
): number => {
  const query = filterText.toLowerCase();
  return blocks.filter(b => getBlockTitle(b).toLowerCase().includes(query)).length;
};

/** Get filtered blocks list (used to resolve selection by index). */
export const getFilteredBlocks = (
  blocks: WebDashboardContentSpecBlocksInner[],
  filterText: string
): WebDashboardContentSpecBlocksInner[] => {
  const query = filterText.toLowerCase();
  return blocks.filter(b => getBlockTitle(b).toLowerCase().includes(query));
};

/** Resolve a block's display title from the typed union. */
export { getBlockTitle as resolveBlockTitle };
